import {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  ScheduleComponent,
  Inject,
  CellClickEventArgs,
  Week,
  ViewsDirective,
  ViewDirective,
  Month,
  TimelineViews,
  TimelineMonth,
  PopupCloseEventArgs,
} from '@syncfusion/ej2-react-schedule';
import { Internationalization, setCulture } from '@syncfusion/ej2-base';
import get from 'lodash/get';
import intl from 'react-intl-universal';
import {
  fetchMySchedule,
  selectMyScheduleCurrentView,
  updateMyScheduleCurrentView,
  selectMyScheduleEvents,
  selectMyScheduleSearchTerm,
  selectFacilitatorsEvents,
} from 'state/Schedule/ScheduleSlice';
import { selectUser } from 'state/User/userSlice';
import { SCHEDULE_TABS } from 'utils/constants/learningSchedule';
import {
  headerTemplate,
  ContentTemplate,
  EventProps,
} from 'Pages/LearningSchedule/components/EventQuickView/EventQuickView';
import '../../styles.css';
import { isEmpty } from 'lodash';
import { getCurrentLocale } from 'utils/localeHelper';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { transformEventForSchedule } from 'Pages/LearningSchedule/helpers/helpers';
import { EventData } from 'utils/types/learningSchedule';
import { useEventColors } from 'Pages/LearningSchedule/components/ScheduleCalendar/hooks/useEventColors';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/ui';
import { chunkArray } from 'Pages/helpers';
import { getUserSummaries } from 'state/normalized/userSummaries/userSummariesSlice';
import { EventsScheduleRef } from '../../../ScheduleCalendar/ScheduleCalendar';
import {
  onAddingEvent,
  resetState,
  loadScheduleLocalization,
} from 'Pages/LearningSchedule/utils';
import {
  getEventPermissions,
  eventPermissionsSelectors,
} from 'state/normalized/eventPermissions/eventPermissionsSlice';
import {
  PopupOpenEventArgs,
  ActionEventArgs,
} from '@syncfusion/ej2-react-schedule';
import { selectFacilitatorsResources } from 'state/Schedule/ScheduleSlice';
import {
  getEventTypeIdFromCode,
  attachListenersToSchedule,
  isSingleDayAllDayEvent,
  processEventTimesForEdit,
  processEventTimesForCreate,
  generateReadableRecurrancePattern,
  getEventData,
  getEventTypeValueFromCode,
} from 'Pages/LearningSchedule/helpers/helpers';
import {
  FetchedEvent,
  isSingleDayEventType,
} from 'utils/types/learningSchedule';
import {
  createEvent,
  editEvent,
  deleteEvent,
  createException,
} from 'state/Schedule/ScheduleSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import {
  serializeCreateEvent,
  serializeUpdateEvent,
  serializeCreateException,
} from 'Pages/LearningSchedule/helpers/helpers';
import isEqual from 'lodash/isEqual';
import {
  DetailsSectionRenderer,
  FacilitatorsSectionRenderer,
} from 'Pages/LearningSchedule/components/Commons';
import { localeMap } from 'Pages/LearningSchedule/helpers/helpers';
import { generateExistingEventData } from 'Pages/LearningSchedule/helpers/scheduleCalendarHelper';
import {
  generateDetailsSectionElements,
  processTimesWhenEventTimezonesEnabled,
} from 'Pages/LearningSchedule/hooks/helpers';
import { useDataForEventCreation } from 'Pages/LearningSchedule/hooks/useCommons';
import { selectUserViewSettingValues } from 'state/ViewSettings/viewSettingsSlice';
import {
  FM_CONFIGURATION_OPTION_KEY,
  FM_CONFIGURATION_OPTION_VALUES,
} from 'utils/constants/fmSettingsModal';
import { useHolidayNavigation } from '../../../../hooks/useHolidayNavigation';

const MyScheduleCalendar = forwardRef<
  EventsScheduleRef,
  { pageToLoad: string }
>(({ pageToLoad }, ref) => {
  loadScheduleLocalization();
  const dispatch = useDispatch();

  const events = useSelector(selectMyScheduleEvents);
  const search = useSelector(selectMyScheduleSearchTerm);
  const currentView = useSelector(selectMyScheduleCurrentView);
  const {
    eventTimezones,
    fmShowWeekends,
    fmExpandedViews,
    fmFacilitatorCreateEvents,
    fmVirtualScrolling,
    fmEventDescription = false,
  } = useFlags();
  const myScheduleObj = useRef<any>(null);
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);
  const { applyCategoryColor, eventsColors } = useEventColors();

  const currentLocale = getCurrentLocale() as keyof typeof localeMap;
  const locale = localeMap[currentLocale] || 'en-US';

  const facilitatorsResources = useSelector(selectFacilitatorsResources);

  const publicHolidaysViewEnabled = useSelector(
    selectUserViewSettingValues(FM_CONFIGURATION_OPTION_KEY)
  );

  const isPublicHolidaysViewEnabled = useMemo(() => {
    return (
      publicHolidaysViewEnabled?.value ===
      FM_CONFIGURATION_OPTION_VALUES.PUBLIC_HOLIDAYS_VIEW_ENABLED
    );
  }, [publicHolidaysViewEnabled?.value]);
  const dispatchErrorNotification = (notificationTitle: string) => {
    dispatch(
      showNotification({
        notificationVariant: 'error',
        notificationTitle,
        notificationMessage: intl.get(
          'SCHEDULE.EVENTS.ERROR_NOTIFICATION_MESSAGE'
        ),
      })
    );
  };

  const currentuser = useSelector(selectUser);
  const countryCode = currentuser?.countryCode;
  const [eventType, setEventType] = useState<string>('');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [eventTitle, setEventTitle] = useState<string>('');
  const [eventDescription, setEventDescription] = useState<string>('');
  const [selectedFacilitatorsIds, setSelectedFacilitatorsIds] = useState<
    string[]
  >([]);
  const [isEditingSeriesWithExceptions, setIsEditingSeriesWithExceptions] =
    useState<boolean>(false);
  const [initialEvent, setInitialEvent] = useState<any>(null);
  const [updatedEvent, setUpdatedEvent] = useState<any>(null);

  useDataForEventCreation();

  const servicesToInject = useMemo(() => {
    if (fmExpandedViews) {
      return [TimelineViews, TimelineMonth, Week, Month];
    }
    return [TimelineViews, Week, Month];
  }, [fmExpandedViews]);

  useEffect(() => {
    async function fetchData(search: string) {
      // Get the current year from the calendar's selected date, or use current year as fallback
      const currentYear = myScheduleObj.current?.selectedDate
        ? new Date(myScheduleObj.current.selectedDate).getFullYear()
        : new Date().getFullYear();

      const data = await dispatch(
        fetchMySchedule({
          search,
          optionalParams: {
            countryCode,
            showPublicHolidays: isPublicHolidaysViewEnabled,
            year: currentYear,
          },
        })
      );
      const formattedSchedules = get(data, 'payload.formattedSchedules');
      if (isEmpty(formattedSchedules) && search) {
        const noRecordsDiv = document.createElement('div');
        noRecordsDiv.textContent = intl.get('NO_RECORDS');
        myScheduleObj.current?.element.childNodes[2].replaceWith(noRecordsDiv);
        for (let i = 0; i <= 2; i++) {
          (
            myScheduleObj.current?.element.childNodes[0].childNodes[0]
              .childNodes[0].childNodes[0].childNodes[i]
              .childNodes[0] as HTMLButtonElement
          ).disabled = true;
        }

        if (noRecordsDiv.style) {
          noRecordsDiv.style.display = 'flex';
          noRecordsDiv.style.justifyContent = 'center';
          noRecordsDiv.style.alignItems = 'center';
          noRecordsDiv.style.height = '100%';
          noRecordsDiv.style.fontSize = '0.875rem';
        }
      } else {
        myScheduleObj.current?.refresh();
      }
    }

    if (pageToLoad === SCHEDULE_TABS.MY_SCHEDULE) {
      fetchData(search);
    }
  }, [pageToLoad, dispatch, search, countryCode, isPublicHolidaysViewEnabled]);

  useEffect(() => {
    const eventCreators = events
      .map((e) => e.createdBy)
      .filter((creator) => creator !== undefined) as string[];

    if (eventCreators.length === 0) return;

    const uniqueUserIds = [...new Set(eventCreators)];
    const chunks = chunkArray(uniqueUserIds, DEFAULT_PAGE_SIZE);

    chunks.forEach((chunk) => {
      dispatch(getUserSummaries({ userIds: chunk }));
    });
  }, [dispatch, events]);

  const eventsDataSource = useMemo(() => {
    const transformEvent = (event: EventData) => ({
      ...event,
      StartTime: new Date(event.StartTime),
      EndTime: new Date(event.EndTime),
      IsAllDay: event.IsAllDay,
    });

    const transformedEvents = eventTimezones
      ? events.map(transformEventForSchedule)
      : events.map(transformEvent);

    return transformedEvents;
  }, [events, eventTimezones]);
  const intlInstance = useMemo(() => {
    return new Internationalization();
  }, []);

  const closeQuickInfo = useCallback(() => {
    if (
      myScheduleObj &&
      myScheduleObj.current &&
      myScheduleObj.current.closeQuickInfoPopup
    ) {
      myScheduleObj.current.closeQuickInfoPopup();
    }
  }, [myScheduleObj]);

  const formatHeader = useCallback(
    (args: { date: Date }) => {
      const formattedDate = intlInstance.formatDate(args?.date, {
        skeleton: 'Ed',
      });
      return <span>{formattedDate}</span>;
    },
    [intlInstance]
  );

  const onUpdateEventField = (key: string, value: any) => {
    setUpdatedEvent((prev: any) => {
      if (prev && prev.hasOwnProperty(key)) {
        return {
          ...prev,
          [key]: value,
        };
      } else {
        return prev;
      }
    });
  };

  useImperativeHandle(ref, () => ({
    onAddingEvent: () => {
      onAddingEvent(undefined, myScheduleObj);
    },
  }));

  const eventPermissionsSelector = useSelector(
    eventPermissionsSelectors.selectEventPermissionsById
  );
  const facilitatorsEvents = useSelector(selectFacilitatorsEvents);
  useEffect(() => {
    const eventIds = events.map((event) => event.Id);
    const facilitatorsEventIds = facilitatorsEvents.map((event) => event.Id);
    const setOfIds = new Set([...eventIds, ...facilitatorsEventIds]);
    const scheduleIds = Array.from(setOfIds);

    if (scheduleIds.length) {
      dispatch(getEventPermissions({ scheduleIds }));
    }
  }, [dispatch, events, facilitatorsEvents]);

  const oncancel = (args: PopupCloseEventArgs) => {
    if (args.type === 'Editor') {
      setUpdatedEvent(null);
      setInitialEvent(null);
      if (args.element.querySelector('.details-section')) {
        const detailsSection = args.element.querySelector('.details-section');
        detailsSection?.remove();
        const facilitatorsSection = args.element.querySelector(
          '.facilitators-section'
        );
        facilitatorsSection?.remove();
      }
    }
  };
  const onPopupOpen = (args: PopupOpenEventArgs) => {
    const eventPermissions = eventPermissionsSelector(args.data?.Id);
    const canUserEdit = eventPermissions?.canEdit;
    const canUserDelete = eventPermissions?.canDelete;
    const quickPopup = document.querySelector('.e-quick-popup-wrapper');
    if (quickPopup) {
      const editButton = quickPopup.querySelector(
        '.e-event-edit'
      ) as HTMLElement;
      const deleteButton = quickPopup.querySelector(
        '.e-event-delete'
      ) as HTMLElement;
      if (editButton && !canUserEdit) editButton.style.display = 'none';
      if (editButton && canUserEdit)
        editButton.textContent = intl.get('SCHEDULE.QUICK_VIEW.VIEW_DETAILS');
      if (deleteButton && !canUserDelete) deleteButton.style.display = 'none';
    }
    let existingEventData: FetchedEvent | undefined;
    let initialProjects: string[] =
      args.data?.ProjectId && args.target ? [args.data?.ProjectId] : [];
    let projectsData: any[] = [];
    let initialFacilitators: { id: string; name: string }[] = [];
    if (args.type === 'RecurrenceValidationAlert') {
      setIsEditingSeriesWithExceptions(true);
    }
    if (args.type === 'Editor') {
      if (args.data?.Id) {
        if (!canUserEdit) {
          args.cancel = true;
          return;
        }
        if (!canUserDelete) {
          const deleteButton = args.element.querySelector(
            '.e-event-delete'
          ) as HTMLElement;
          if (deleteButton) {
            deleteButton.style.display = 'none';
          }
        }
      }
      if (args.data?.FacilitatorId && args.target) {
        const facilitatorData = facilitatorsResources.find(
          (f) => f.FacilitatorId === args.data?.FacilitatorId
        );
        if (facilitatorData) {
          initialFacilitators = [
            { id: facilitatorData.FacilitatorId, name: facilitatorData.Name },
          ];
        }
      }
      if (args.data?.Id) {
        if (args.data?.Subject) {
          const modalTitle = args.element.querySelector('.e-title-text');
          if (modalTitle) {
            modalTitle.textContent = intl.get('SCHEDULE.EDIT_EVENT_TITLE', {
              eventTitle: args.data.Subject,
            });
          }
        }
        const isRecurringEvent = args?.data?.RecurrenceRule !== null;
        let startTime = args.data?.StartTime;
        let endTime = args.data?.EndTime;

        // For multi-day all-day events, EndTime was adjusted +1 for Syncfusion display
        // Subtract 1 day to get the actual end date for editing
        if (args.data?.IsAllDay && startTime && endTime) {
          const startDate = new Date(startTime).toDateString();
          const endDate = new Date(endTime).toDateString();
          if (startDate !== endDate) {
            const adjustedEndTime = new Date(endTime);
            adjustedEndTime.setDate(adjustedEndTime.getDate() - 1);
            endTime = adjustedEndTime;
          }
        }

        let isOldEvent = args.data?.isOldEvent
          ? false
          : args.data?.isOldEvent || true;

        processTimesWhenEventTimezonesEnabled(
          eventTimezones,
          args,
          startTime,
          endTime,
          isRecurringEvent
        );

        const eventTypeId = getEventTypeIdFromCode(args.data?.EventType ?? '');
        existingEventData = generateExistingEventData(
          args,
          eventTypeId,
          startTime,
          endTime,
          isOldEvent
        );
        initialFacilitators = args.data?.facilitators || [];
        initialProjects = existingEventData?.projectIds || [];
        setInitialEvent(existingEventData);
        setUpdatedEvent(existingEventData);
        setEventTitle(existingEventData?.subject || '');
        setEventDescription(existingEventData?.description ?? '');
        setEventType(existingEventData?.learningEventType || '');
        projectsData = existingEventData?.projects || [];
      }
      setProjectIds(initialProjects);
      attachListenersToSchedule(args, onUpdateEventField);

      if (!args.element.querySelector('.details-section')) {
        if (myScheduleObj) {
          setCulture(locale);
          const recurrenceEditor =
            myScheduleObj?.current?.eventWindow?.recurrenceEditor;
          if (recurrenceEditor) {
            recurrenceEditor.frequencies = [
              'none',
              'daily',
              'weekly',
              'monthly',
            ];
          }
        }
        const { detailsContainer, facilitatorsContainer } =
          generateDetailsSectionElements(args, saveButtonRef);

        DetailsSectionRenderer({
          existingEventData: existingEventData,
          projectsData: projectsData,
          fmEventDescription,
          setEventTitle: setEventTitle,
          setEventDescription: setEventDescription,
          setProjectIds: setProjectIds,
          setEventType: setEventType,
          onUpdateEventField: onUpdateEventField,
          initialProjects: initialProjects,
          detailsContainer: detailsContainer,
        });

        FacilitatorsSectionRenderer({
          initialFacilitators: initialFacilitators,
          facilitatorsContainer: facilitatorsContainer,
          setSelectedFacilitatorsIds: setSelectedFacilitatorsIds,
          onUpdateEventField: onUpdateEventField,
        });
      }
    } else if (args.type === 'RecurrenceAlert') {
      if (!canUserEdit) {
        args.cancel = true;
        return;
      }
      const dialogHeader = args.element.querySelector('#QuickDialog_title');
      const dialogContent = args.element.querySelector(
        '#QuickDialog_dialog-content'
      );
      const seriesButton = args.element.querySelector(
        'button.e-quick-dialog-series-event'
      );
      if (dialogContent && seriesButton) {
        if (dialogHeader?.textContent === 'Delete Event') {
          dialogContent.textContent = intl.get(
            'SCHEDULE.EVENTS.DELETE_RECURRING_EVENT_MESSAGE'
          );
          seriesButton.textContent = intl.get(
            'SCHEDULE.EVENTS.DELETE_SERIES_BUTTON'
          );
        } else if (dialogHeader?.textContent === 'Edit Event') {
          dialogContent.textContent = intl.get(
            'SCHEDULE.EVENTS.EDIT_RECURRING_EVENT_MESSAGE'
          );
          seriesButton.textContent = intl.get(
            'SCHEDULE.EVENTS.EDIT_SERIES_BUTTON'
          );
        }
      }
    }
  };

  const actionBegin = async (args: ActionEventArgs) => {
    // Helper function to check if an event is a single-day all-day event
    if (args.requestType === 'eventChange' && args?.changedRecords?.length) {
      try {
        if (args?.data?.hasOwnProperty('occurrence')) {
          const isRecurringEvent = true;
          let modifiedEventType: string = isNaN(Number(eventType))
            ? getEventTypeValueFromCode(eventType)
            : eventType;
          const data = args.changedRecords[0] as EventData;
          const parent = get(args, 'data.parent', null);
          let startTime = data.StartTime;
          let endTime = data.EndTime;
          let isOldEvent = data.isOldEvent ? false : data.isOldEvent;
          if (eventTimezones) {
            const isSingleDayEvent = isSingleDayAllDayEvent(data);
            const result = processEventTimesForEdit(
              data,
              isSingleDayEvent,
              isRecurringEvent
            );
            startTime = result.startTime;
            endTime = result.endTime;
          }
          if (parent) {
            let actionDispatched = false;
            const t = setInterval(async () => {
              if (parent.RecurrenceException) {
                const exceptionEvent = getEventData({
                  projectIds,
                  eventTitle,
                  eventDescription,
                  eventType: modifiedEventType,
                  data: {
                    ...data,
                    isOldEvent,
                    StartTime: startTime,
                    EndTime: endTime,
                    ...(eventTimezones && {
                      CreatorTimeZone:
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                    }),
                  },
                  facilitators: selectedFacilitatorsIds,
                });
                const serializedException = serializeCreateException(
                  exceptionEvent,
                  parent.Id,
                  parent.RecurrenceException
                );
                try {
                  if (!actionDispatched) {
                    actionDispatched = true;
                    await dispatch(
                      createException({
                        scheduleId: parent.Id,
                        data: serializedException,
                      })
                    );
                  }
                } catch {
                  dispatchErrorNotification(
                    intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
                  );
                }
                clearInterval(t);
              }
            }, 10);
          }
        } else {
          const data = args.changedRecords[0] as EventData;
          let shouldResetExceptions: boolean = false;
          if (data) {
            let actionDispatched = false;
            const t = setInterval(async () => {
              if (isEditingSeriesWithExceptions && !data.RecurrenceException) {
                shouldResetExceptions = true;
              }
              let modifiedEventType: string = isNaN(Number(eventType))
                ? getEventTypeValueFromCode(eventType)
                : eventType;
              let startTime = data.StartTime;
              let endTime = data.EndTime;
              let isOldEvent = data.isOldEvent ? false : data.isOldEvent;
              if (eventTimezones) {
                const isSingleDayEvent = isSingleDayAllDayEvent(data);
                const result = processEventTimesForEdit(data, isSingleDayEvent);
                startTime = result.startTime;
                endTime = result.endTime;
              }
              const editedEvent = getEventData({
                projectIds,
                eventTitle,
                eventDescription,
                eventType: modifiedEventType,
                data: {
                  ...data,
                  RecurrenceRuleFriendlyName: generateReadableRecurrancePattern(
                    data.RecurrenceRule
                  ),
                  OldRecurrenceRuleFriendlyName:
                    generateReadableRecurrancePattern(
                      (args.data as EventData)?.RecurrenceRule
                    ),
                  StartTime: startTime,
                  EndTime: endTime,
                  isOldEvent,
                  ...(eventTimezones && {
                    CreatorTimeZone:
                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                  }),
                },
                facilitators: selectedFacilitatorsIds,
              });
              const editEventParams = serializeUpdateEvent(
                editedEvent,
                shouldResetExceptions
              );
              try {
                if (!actionDispatched) {
                  actionDispatched = true;
                  await dispatch(editEvent(editEventParams));
                }
              } catch {
                dispatchErrorNotification(
                  intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
                );
              }
              clearInterval(t);
            }, 10);
          }
        }

        dispatch(
          showNotification({
            notificationVariant: 'success',
            notificationTitle: intl.get('SCHEDULE.EVENTS.EDIT_SUCCESS'),
            autoHide: false,
          })
        );
      } catch {
        dispatchErrorNotification(
          intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
        );
      }
    } else if (args.requestType === 'eventCreate') {
      if (args.data && args.data.length) {
        try {
          const data = args.data as EventData[];

          // Check if it's a single-day all-day event
          const isSingleDayEvent = isSingleDayAllDayEvent(data[0]);
          let startTime = data[0].StartTime;
          let endTime = data[0].EndTime;
          if (eventTimezones) {
            const result = processEventTimesForCreate(
              data[0] as isSingleDayEventType,
              isSingleDayEvent
            );
            startTime = result.startTime;
            endTime = result.endTime;
          }
          const newEvent = {
            StartTime: startTime,
            EndTime: endTime,
            RecurrenceRule: data[0].RecurrenceRule,
            RecurrenceRuleFriendlyName: generateReadableRecurrancePattern(
              data[0].RecurrenceRule
            ),
            Id: data[0].Id,
            ProjectId: projectIds,
            FacilitatorId: selectedFacilitatorsIds,
            Subject: eventTitle,
            Description: eventDescription || null,
            EventType: eventType,
            IsAllDay: data[0].IsAllDay,
            isOldEvent: false,
            ...(eventTimezones && {
              CreatorTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          } as EventData;

          args.cancel = true;
          const createEventParams = serializeCreateEvent(newEvent);
          await dispatch(
            createEvent({
              ...createEventParams,
            })
          );
          dispatch(
            showNotification({
              notificationVariant: 'success',
              notificationTitle: intl.get('SCHEDULE.EVENTS.CREATE_SUCCESS'),
              autoHide: false,
            })
          );
          // Reset state after successful event creation
          resetState({
            setProjectIds,
            setEventTitle,
            setEventDescription,
            setEventType,
            setSelectedFacilitatorsIds,
            setIsEditingSeriesWithExceptions,
          });
          // Close the editor modal after successful event creation
          if (myScheduleObj.current && myScheduleObj.current.closeEditor) {
            myScheduleObj.current.closeEditor();
          }
        } catch {
          dispatchErrorNotification(
            intl.get('SCHEDULE.EVENTS.CREATE_ERROR_NOTIFICATION_TITLE')
          );
        }
      }
    } else if (args.requestType === 'eventRemove') {
      try {
        const data = args.data;

        const changedRecords = args.changedRecords;
        if (Array.isArray(data) && data.length > 0) {
          const parent = data[0].parent;
          const occurrence = data[0].occurrence;
          if (parent && occurrence && parent.Id === occurrence.Id) {
            let startTime = data[0].parent.StartTime;
            let endTime = data[0].parent.EndTime;
            let isOldEvent = data[0].parent.isOldEvent
              ? false
              : data[0].parent.isOldEvent;
            if (eventTimezones) {
              startTime = new Date(startTime);
              endTime = new Date(endTime);
            }
            let actionDispatched = false;
            const t = setInterval(async () => {
              if (parent.RecurrenceException) {
                args.cancel = true;
                const modifiedEventType: string = getEventTypeValueFromCode(
                  parent.EventType
                );
                const editEventParams = serializeUpdateEvent({
                  ...parent,
                  isOldEvent,
                  EventType: modifiedEventType,
                  Facilitators: [],
                  StartTime: startTime,
                  EndTime: endTime,
                });
                try {
                  if (!actionDispatched) {
                    actionDispatched = true;
                    await dispatch(editEvent(editEventParams));
                  }
                } catch {
                  dispatchErrorNotification(
                    intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
                  );
                }
                clearInterval(t);
              }
            }, 10);
          } else {
            if (changedRecords && changedRecords.length) {
              await dispatch(deleteEvent(changedRecords[0].Id));
            } else {
              await dispatch(deleteEvent(data[0].Id));
            }
          }
        }
        dispatch(
          showNotification({
            notificationVariant: 'success',
            notificationTitle: intl.get('SCHEDULE.EVENTS.DELETE_SUCCESS'),
            autoHide: false,
          })
        );
      } catch {
        dispatchErrorNotification(
          intl.get('SCHEDULE.EVENTS.DELETE_ERROR_NOTIFICATION_TITLE')
        );
      }
    }
  };

  // Handle save button state for editing existing events
  useEffect(() => {
    if (saveButtonRef?.current) {
      if (
        updatedEvent &&
        initialEvent &&
        !isEqual(updatedEvent, initialEvent) &&
        updatedEvent.subject &&
        updatedEvent.learningEventType
      ) {
        saveButtonRef.current.disabled = false;
      } else {
        saveButtonRef.current.disabled = true;
      }
    }
  }, [updatedEvent, initialEvent]);

  // Handle save button state for creating new events
  useEffect(() => {
    if (updatedEvent || initialEvent) {
      return;
    }
    if (eventTitle && eventType) {
      if (saveButtonRef && saveButtonRef.current) {
        saveButtonRef.current.disabled = false;
      }
    } else {
      if (saveButtonRef && saveButtonRef.current) {
        saveButtonRef.current.disabled = true;
      }
    }
  }, [eventTitle, eventType, saveButtonRef, updatedEvent, initialEvent]);

  const onCellClick = (args: CellClickEventArgs) => {
    args.cancel = true;
  };

  // Use shared holiday navigation hook
  const {
    handleNavigating,
    handleActionComplete,
    handleDataBound: handleHolidayDataBound,
  } = useHolidayNavigation({
    scheduleObj: myScheduleObj,
    pageToLoad,
    isPublicHolidaysViewEnabled,
    countryCode,
    search,
    onDataBound: () => {
      if (myScheduleObj?.current?.currentView) {
        dispatch(
          updateMyScheduleCurrentView({
            currentView: myScheduleObj?.current?.currentView,
          })
        );
      }
    },
  });

  // Enhanced dataBound handler that combines view update and holiday refetch
  const handleDataBound = useCallback(() => {
    handleHolidayDataBound();
  }, [handleHolidayDataBound]);

  setCulture(locale);

  return (
    <div
      className='h-my-schedule overflow-y-auto'
      data-testid='my-schedule_container'
      id='my-schedule'
    >
      <ScheduleComponent
        locale={locale}
        timezone='UTC'
        height='100%'
        ref={myScheduleObj}
        eventSettings={{
          dataSource: eventsDataSource,
          allowEditing: fmFacilitatorCreateEvents,
          allowAdding: fmFacilitatorCreateEvents,
        }}
        actionBegin={actionBegin}
        actionComplete={handleActionComplete}
        popupClose={oncancel}
        actionFailure={actionBegin}
        popupOpen={onPopupOpen}
        navigating={handleNavigating}
        currentView={currentView}
        showTimeIndicator={false}
        rowAutoHeight={true}
        dateHeaderTemplate={formatHeader}
        cellClick={(args) => onCellClick(args)}
        eventDoubleClick={(args) => (args.cancel = true)}
        quickInfoTemplates={{
          header: headerTemplate(closeQuickInfo),
          content: (props: EventProps) =>
            ContentTemplate({
              ...props,
              eventColor: eventsColors[props.Id],
            }),
          ...(!fmFacilitatorCreateEvents && { footer: () => <></> }),
        }}
        eventRendered={applyCategoryColor}
        dataBound={handleDataBound}
      >
        <ViewsDirective>
          <ViewDirective
            option='Week'
            displayName='Week'
            showWeekend={fmShowWeekends}
          />
          {fmExpandedViews && (
            <ViewDirective
              option='TimelineMonth'
              allowVirtualScrolling={fmVirtualScrolling}
              showWeekend={fmShowWeekends}
              displayName='Month'
              orientation='Vertical'
            />
          )}
          <ViewDirective
            option='Month'
            displayName='Monthly Calendar'
            showWeekend={fmShowWeekends}
          />
        </ViewsDirective>
        <Inject services={servicesToInject} />
      </ScheduleComponent>
    </div>
  );
});

export default MyScheduleCalendar;
